import { Component, OnInit, OnDestroy, ViewChild, ChangeDetectorRef, HostListener, ElementRef, TemplateRef } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { ToastrService } from 'ngx-toastr';
import { MatDialog } from '@angular/material/dialog';
import { AlternateWaterCapacityService, AlternateWaterCapacityRow, AlternateWaterCapacityDetailRequest } from '../../services/alternate-water-capacity.service';
import { TableColumn } from '../../services/interfaces/alternate-water-capacity.interfaces';
import { AuthService } from '../../services/auth.service';
import { ErrorHandler } from '../../utils/error-handler';
import { exportRowsToExcel } from '../../utils/excel-export';

@Component({
  selector: 'app-alternate-water-capacity',
  standalone: false,
  templateUrl: './alternate-water-capacity.component.html',
  styleUrls: [
    '../water-capacities-view/water-capacities-view.component.scss',
    './alternate-water-capacity.component.scss'
  ]
})
export class AlternateWaterCapacityComponent implements OnInit, OnDestroy {
  @ViewChild(CdkVirtualScrollViewport) viewport!: CdkVirtualScrollViewport;
  @ViewChild('floatingFilterPanel') floatingFilterPanel!: ElementRef;
  @ViewChild('deleteConfirmDialog') deleteConfirmDialog!: TemplateRef<any>;
  @ViewChild('exportOptionsDialog') exportOptionsDialog!: TemplateRef<any>;

  spinnerBoolean: boolean = false;
  data: AlternateWaterCapacityRow[] = [];
  selectedRows: AlternateWaterCapacityRow[] = [];
  showCancel: boolean = false;
  dropdownLoading: boolean = false;
  loading: boolean = false;
  hoveredRowIndex: number | null = null;

  // Pagination tracking
  nextRowNumber: number | null = 0;
  hasMore: boolean = true;

  // Dropdown options from service
  devAreaOptions: string[] = [];
  feAssetNameOptions: string[] = [];
  disposalOutletOptions: string[] = [];
  alternativeCapacityTypeOptions: string[] = [];

  // Filter-related properties
  filterColumnsData: { [key: string]: string[] } = {};
  isLoadingFilterOptions: boolean = false;
  filterSearch: { [key: string]: string } = {};
  activeFilter: string = '';
  showFloatingFilter: boolean = false;
  dropdownPosition = { top: 0, left: 0 };
  multiFilters: { [key: string]: string[] } = {};
  currentFilters: { [key: string]: string | null } = {
    devArea: null,
    feAssetName: null,
    disposalOutlet: null,
    alternativeCapacityType: null,
  };

  // Date range filters for effective date
  dateRangeFilters: {
    startDate: string | null;
    endDate: string | null;
  } = {
    startDate: null,
    endDate: null,
  };

  // Search filter for capacity value
  capacitySearchFilter: string | null = null;

  public dialogMessage: string = '';
  private destroy$ = new Subject<void>();

  columns: TableColumn[] = [
    { field: 'devArea', header: 'Dev Area', required: true, locked: true, filterable: true, filterType: 'multi-select', allowMultiple: true },
    { field: 'feAssetName', header: 'FE Asset', required: true, locked: true, filterable: true, filterType: 'multi-select', allowMultiple: true },
    { field: 'disposalOutlet', header: 'Disposal Outlet', required: true, locked: true, filterable: true, filterType: 'multi-select', allowMultiple: true },
    { field: 'alternativeCapacityType', header: 'Alternate Capacity Type', required: true, locked: true, filterable: true, filterType: 'multi-select' },
    { field: 'alternativeCapacityValue', header: 'Alternate Capacity', required: true, filterable: true, filterType: 'search' },
    { field: 'effectiveDate', header: 'Effective Date', required: true, filterable: true, filterType: 'date-range' }
  ];

  isIdentityFieldEditable(row: AlternateWaterCapacityRow): boolean {
    return row.isNew;
  }

  constructor(
    private toastr: ToastrService,
    private alternateWaterService: AlternateWaterCapacityService,
    private authService: AuthService,
    private cdr: ChangeDetectorRef,
    private elementRef: ElementRef,
    private dialog: MatDialog
  ) {}

  async ngOnInit(): Promise<void> {
    await this.loadCapacities();
    await this.loadInitialDropdowns();
    await this.loadFilterOptionsInit();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async loadInitialDropdowns(): Promise<void> {
    try {
      this.dropdownLoading = true;
      const dropdownData = await this.alternateWaterService.getAllDropdownOptions({
        developmentArea: null,
        feAssetName: null,
        disposalOutletName: null,
      });
      
      this.devAreaOptions = dropdownData.devAreas || [];
      this.feAssetNameOptions = dropdownData.feAssetNames || [];
      this.disposalOutletOptions = dropdownData.disposalOutlets || [];
      this.alternativeCapacityTypeOptions = dropdownData.alternativeCapacityTypes || [];

    } catch (error: any) {
      const errorMessage = error.message || 'Failed to load dropdown options';
      this.toastr.error(errorMessage, 'Error');
    } finally {
      this.dropdownLoading = false;
    }
  }

  private setFilterColumnsData(devAreas: string[], feAssetNames: string[], disposalOutlets: string[], alternativeCapacityTypes: string[]): void {
    this.filterColumnsData['devArea'] = [...devAreas];
    this.filterColumnsData['feAssetName'] = [...feAssetNames];
    this.filterColumnsData['disposalOutlet'] = [...disposalOutlets];
    this.filterColumnsData['alternativeCapacityType'] = [...alternativeCapacityTypes];
  }

  async loadFilterOptionsInit(): Promise<void> {
    this.isLoadingFilterOptions = true;
    try {
      const filterData = await this.alternateWaterService.getFilterOptionsBySelection({
        developmentArea: null,
        feAssetName: null,
        disposalOutletName: null,
      });

      this.setFilterColumnsData(
        filterData.devAreas || [],
        filterData.feAssetNames || [],
        filterData.disposalOutlets || [],
        filterData.alternativeCapacityTypes || []
      );
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to load filter options';
      this.toastr.error(errorMessage, 'Error');
    } finally {
      this.isLoadingFilterOptions = false;
    }
  }

  async reloadFilterOptions(): Promise<void> {
    this.isLoadingFilterOptions = true;
    try {
      const filterData = await this.alternateWaterService.getFilterOptionsBySelection({
        developmentArea: this.currentFilters['devArea'] || null,
        feAssetName: this.currentFilters['feAssetName'] || null,
        disposalOutletName: this.currentFilters['disposalOutlet'] || null,
      });

      this.setFilterColumnsData(
        filterData.devAreas || [],
        filterData.feAssetNames || [],
        filterData.disposalOutlets || [],
        filterData.alternativeCapacityTypes || []
      );
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to reload filter options';
      this.toastr.error(errorMessage, 'Error');
    } finally {
      this.isLoadingFilterOptions = false;
    }
  }

  private mapColumnNameToFieldName(columnName: string): string {
    const columnMap: { [key: string]: string } = {
      'DevelopmentArea': 'devArea',
      'DevArea': 'devArea',
      'FEAssetName': 'feAssetName',
      'FeAssetName': 'feAssetName',
      'DisposalOutletName': 'disposalOutlet',
      'DisposalOutlet': 'disposalOutlet',
      'AlternativeCapacityType': 'alternativeCapacityType',
      'AlternateCapacityType': 'alternativeCapacityType'
    };

    return columnMap[columnName] || columnName;
  }

  private extractDeleteStatus(payload: any): { statusCode: number; statusMessage: string; success: boolean } {
    const nestedStatus = payload?.statusMessage && typeof payload.statusMessage === 'object'
      ? payload.statusMessage
      : null;
    const parsed = ErrorHandler.parseApiResult(nestedStatus || payload);
    const statusCode =
      parsed.statusCode ||
      nestedStatus?.statusCode ||
      payload?.statusCode ||
      payload?.status ||
      500;
    const statusMessage =
      typeof parsed.message === 'string' && parsed.message.trim().length > 0
        ? parsed.message
        : nestedStatus?.statusMessage ||
          payload?.statusMessage ||
          'Unknown error occurred';
    const success =
      parsed.success ||
      payload?.error === false ||
      statusCode === 200 ||
      statusCode === 201 ||
      statusCode === 202 ||
      statusCode === 204;

    return {
      statusCode,
      statusMessage,
      success,
    };
  }

  async loadCapacities(filters?: Partial<AlternateWaterCapacityDetailRequest>): Promise<void> {
    try {
      this.spinnerBoolean = true;
      
      const filterPayload: AlternateWaterCapacityDetailRequest = {
        developmentArea: this.currentFilters['devArea'] || null,
        feAssetName: this.currentFilters['feAssetName'] || null,
        disposalOutletName: this.currentFilters['disposalOutlet'] || null,
        alternativeCapacityType: this.currentFilters['alternativeCapacityType'] || null,
        startDate: this.dateRangeFilters.startDate || null,
        endDate: this.dateRangeFilters.endDate || null,
        lastRowNumber: filters?.lastRowNumber ?? 0,
        ...filters
      };

      const response = await this.alternateWaterService.getCapacitiesWithPagination(filterPayload);
      
      this.nextRowNumber = response.nextRowNumber ?? null;
      this.hasMore = this.nextRowNumber !== null && this.nextRowNumber > (filterPayload.lastRowNumber ?? 0);

      if (response.capacities && response.capacities.length > 0) {
        let newRows = response.capacities.map((capacity): AlternateWaterCapacityRow => ({
          _rowId: this.generateRowId(),
          devArea: capacity.devArea,
          feAssetName: capacity.feAssetName,
          disposalOutlet: capacity.disposalOutlet,
          alternativeCapacityType: capacity.alternativeCapacityType,
          alternativeCapacityValue: capacity.alternativeCapacityValue || null,
          effectiveDate: capacity.effectiveDate,
          isNew: false,
          _rowEdited: false,
          original: {}
        }));

        // Apply client-side capacity search filter if set
        if (this.capacitySearchFilter && this.capacitySearchFilter.trim() !== '') {
          const searchValue = this.capacitySearchFilter.toLowerCase().trim();
          newRows = newRows.filter(row => {
            const capacityStr = row.alternativeCapacityValue?.toString().toLowerCase() || '';
            return capacityStr.includes(searchValue);
          });
        }

        for (const row of newRows) {
          row.original = { ...row };
        }

        if (filterPayload.lastRowNumber === 0) {
          this.data = newRows;
        } else {
          this.mergeRows(newRows);
        }

      } else {
        if (filterPayload.lastRowNumber === 0) {
          this.data = [];
          if (!this.hasAnyFilterApplied()) {
            this.toastr.info('No existing alternate water capacity data found', 'Info');
          }
        }
      }
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to load alternate water capacities';
      this.toastr.error(errorMessage, 'Error');
      this.hasMore = false;
    } finally {
      this.spinnerBoolean = false;
      this.loading = false;
    }
  }

  private mergeRows(newRows: AlternateWaterCapacityRow[]): void {
    const keyOf = (r: AlternateWaterCapacityRow) =>
      `${r.devArea}||${r.feAssetName}||${r.disposalOutlet}||${r.alternativeCapacityType}||${r.effectiveDate}`;

    const existingKeys = new Set(this.data.map(keyOf));
    const toAdd = newRows.filter((r) => !existingKeys.has(keyOf(r)));
    this.data = [...this.data, ...toAdd];
  }

  loadFirstPage(): void {
    this.data = [];
    this.nextRowNumber = 0;
    this.hasMore = true;
    this.loadCapacities({ lastRowNumber: 0 });
  }

  onScrolledIndexChange(index: number): void {
    const buffer = 10;
    if (
      this.hasMore &&
      !this.loading &&
      !this.spinnerBoolean &&
      index >= Math.max(0, this.data.length - buffer)
    ) {
      this.loading = true;
      this.loadCapacities({ lastRowNumber: this.nextRowNumber ?? 0 });
    }
  }

  async handleSave(): Promise<void> {
    const userName = this.authService.getUserName();
    let successCount = 0;
    let failureCount = 0;
    let errorMessages: string[] = [];

    try {
      const required = this.columns.filter(col => col.required).map(col => col.field);
      const changedRows = this.data.filter(row => row.isNew || row._rowEdited);
      
      if (changedRows.length === 0) {
        this.toastr.info('No changes to save.');
        return;
      }

      const invalid = changedRows.filter(row => 
        required.some(field => {
          const value = row[field];
          return value === undefined || value === null || value === '';
        })
      );
      
      if (invalid.length) { 
        this.toastr.error('Please fill all required fields before saving.'); 
        return; 
      }

      // Validate percentage values
      const percentageInvalid = changedRows.filter(row => 
        row.alternativeCapacityType === '%' && 
        (row.alternativeCapacityValue === null || row.alternativeCapacityValue < 0 || row.alternativeCapacityValue > 100)
      );

      if (percentageInvalid.length) {
        this.toastr.error('Percentage values must be between 0 and 100.');
        return;
      }

      this.spinnerBoolean = true;
      
      for (const row of changedRows) {
        const action: 'INSERT' | 'UPDATE' = row.isNew ? 'INSERT' : 'UPDATE';

        const response = await this.alternateWaterService.saveCapacity(row, action, userName);
        const parsed = ErrorHandler.parseApiResult(response);

        if (parsed.success) {
          row.isNew = false;
          row._rowEdited = false;
          row.original = { ...row };
          successCount++;
        } else {
          failureCount++;
          errorMessages.push(parsed.message || 'Unknown error occurred');
        }
      }

      await this.loadCapacities();

      if (successCount > 0) {
        this.toastr.success(`${successCount} alternate water capacity row(s) saved successfully`, 'Success');
        this.showCancel = false;
      }

      if (failureCount > 0) {
        const detailedErrorMsg = errorMessages.length > 0
          ? errorMessages.join('<br>')
          : `${failureCount} alternate water capacity row(s) failed to save`;
        
        this.toastr.error(detailedErrorMsg, 'Save Error', {
          timeOut: 8000,
          enableHtml: true
        });
      }
    } catch (error: any) {
      const parsed = ErrorHandler.parseApiResult(error?.error || error);
      this.toastr.error(parsed.message || 'Failed to save alternate water capacities', 'Error');
    } finally {
      this.spinnerBoolean = false;
    }
  }

  async handleDelete(): Promise<void> {
    if (!this.selectedRows.length) { 
      this.toastr.info('Please select rows to delete.'); 
      return; 
    }

    const userName = this.authService.getUserName();

    this.dialogMessage = `Are you sure you want to delete ${this.selectedRows.length} row(s)? This action cannot be undone.`;

    const dialogRef = this.dialog.open(this.deleteConfirmDialog, {
      width: '400px',
      disableClose: true,
    });

    const confirmed = await dialogRef.afterClosed().toPromise();
    if (!confirmed) return;

    let successCount = 0;
    let failureCount = 0;
    let errorMessages: string[] = [];

    try {
      this.spinnerBoolean = true;

      for (const row of this.selectedRows) {
        if (row.isNew) {
          const index = this.data.indexOf(row);
          if (index > -1) {
            this.data.splice(index, 1);
          }
          successCount++;
        } else {
          const response = await this.alternateWaterService.deleteCapacity(row, userName);
          const { success, statusMessage } = this.extractDeleteStatus(response);

          if (success) {
            successCount++;
          } else {
            failureCount++;
            errorMessages.push(statusMessage);
          }
        }
      }

      this.data = this.data.filter(row => !this.selectedRows.includes(row));
      this.selectedRows = [];

      await this.loadCapacities();
      
      if (successCount > 0) {
        this.toastr.success(`${successCount} row(s) deleted successfully.`);
      }
      if (failureCount > 0) {
        const detailedErrorMsg = errorMessages.length > 0
          ? errorMessages.join('<br>')
          : `${failureCount} row(s) failed to delete`;
        
        this.toastr.error(detailedErrorMsg, 'Delete Error', {
          timeOut: 8000,
          enableHtml: true
        });
      }
    } catch (error: any) {
      const { success, statusMessage } = this.extractDeleteStatus(error);

      if (success) {
        this.toastr.success(statusMessage, 'Success');
      } else {
        this.toastr.error(statusMessage, 'Error');
      }
    } finally {
      this.spinnerBoolean = false;
    }
  }

  cancelRow(): void {
    this.data = this.data.filter((row) => !row.isNew);
    
    this.data.forEach(row => {
      if (row._rowEdited && row.original && Object.keys(row.original).length > 0) {
        Object.assign(row, row.original);
        row._rowEdited = false;
      }
    });
    
    this.showCancel = false;
    this.clearSelection();
    
    this.cdr.detectChanges();
    
    setTimeout(() => {
      this.viewport?.checkViewportSize();
    });
    
    this.toastr.info('Changes cancelled.');
  }

  async addRow(): Promise<void> {
    if (!this.devAreaOptions.length || !this.feAssetNameOptions.length || !this.disposalOutletOptions.length) {
      await this.loadInitialDropdownsForNewRow();
    }

    const newRow: AlternateWaterCapacityRow = { 
      _rowId: this.generateRowId(), 
      devArea: '', 
      feAssetName: '', 
      disposalOutlet: '', 
      alternativeCapacityType: '', 
      alternativeCapacityValue: null, 
      effectiveDate: '', 
      isNew: true, 
      _rowEdited: false, 
      original: {} 
    };
    
    this.data = [newRow, ...this.data];
    this.showCancel = true;
    this.clearSelection();
    
    this.cdr.detectChanges();
    setTimeout(() => {
      this.viewport?.checkViewportSize();
      this.viewport?.scrollToIndex(0, 'smooth');
    });
    
    this.toastr.info('New alternate water capacity row added. Click Cancel to discard or Save to keep changes.');
  }

  copyRow(index: number): void {
    const originalRow = this.data[index];
    if (!originalRow) return;

    const copiedRow: AlternateWaterCapacityRow = {
      _rowId: this.generateRowId(),
      devArea: originalRow.devArea,
      feAssetName: originalRow.feAssetName,
      disposalOutlet: originalRow.disposalOutlet,
      alternativeCapacityType: originalRow.alternativeCapacityType,
      alternativeCapacityValue: originalRow.alternativeCapacityValue,
      effectiveDate: '',
      isNew: true,
      _rowEdited: false,
      original: {}
    };

    this.data = [copiedRow, ...this.data];
    this.showCancel = true;
    this.clearSelection();

    this.cdr.detectChanges();
    setTimeout(() => {
      this.viewport?.checkViewportSize();
      this.viewport?.scrollToIndex(0, 'smooth');
    }, 100);

    this.toastr.info('Row copied successfully. Click Cancel to discard or Save to keep changes.');
  }

  clearSelection(): void {
    this.selectedRows = [];
  }

  toggleRowSelection(row: AlternateWaterCapacityRow, ev: Event): void {
    const target = ev.target as HTMLInputElement;
    if (target.checked) { 
      if (!this.selectedRows.includes(row)) this.selectedRows.push(row); 
    } else { 
      const index = this.selectedRows.indexOf(row); 
      if (index > -1) this.selectedRows.splice(index, 1); 
    }
  }

  markAsEdited(row: AlternateWaterCapacityRow): void {
    if (!row.original || Object.keys(row.original).length === 0) {
      row.original = { ...row };
    }
    if (!row.isNew) {
      row._rowEdited = true;
      this.showCancel = true;
    }
  }

  handleEdit(row: AlternateWaterCapacityRow): void { 
    this.markAsEdited(row);
  }

  validateCapacity(row: AlternateWaterCapacityRow, ev: Event): void {
    const target = ev.target as HTMLInputElement;
    const value = parseFloat(target.value);
    row.alternativeCapacityValue = isNaN(value) ? null : value;
    
    // Validate percentage range
    if (row.alternativeCapacityType === '%' && value > 100) {
      row.alternativeCapacityValue = 100;
      target.value = '100';
      this.toastr.warning('Percentage values cannot exceed 100%');
    }
    
    this.markAsEdited(row);
  }

  onCapacityTypeChange(row: AlternateWaterCapacityRow): void {
    // Reset capacity value if switching between types and current value is invalid
    if (row.alternativeCapacityType === '%' && row.alternativeCapacityValue && row.alternativeCapacityValue > 100) {
      row.alternativeCapacityValue = null;
    }
    this.markAsEdited(row);
  }

  // Filter methods
  async openFloatingFilter(event: MouseEvent, field: string): Promise<void> {
    event.stopPropagation();

    if (this.showFloatingFilter && this.activeFilter === field) {
      this.closeFloatingFilter();
      return;
    }

    if (this.getFilterType(field) === 'multi-select') {
      try {
        await this.reloadFilterOptions();
      } catch (error: any) {
        this.toastr.error(error.message || 'Failed to refresh filter options', 'Error');
      }
    }

    const target = event.target as HTMLElement;
    const rect = target.getBoundingClientRect();
    this.dropdownPosition = {
      top: rect.bottom - 110 + window.scrollY,
      left: rect.left - 10 + window.scrollX,
    };
    this.activeFilter = field;
    this.showFloatingFilter = true;
  }

  closeFloatingFilter(): void {
    this.showFloatingFilter = false;
    this.activeFilter = '';
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.showFloatingFilter) {
      const target = event.target as HTMLElement;
      const clickedInside = this.floatingFilterPanel?.nativeElement.contains(target);
      const clickedOnFilterIcon = target.classList.contains('filter-icon') ||
        target.parentElement?.classList.contains('filter-icon');

      if (!clickedInside && !clickedOnFilterIcon) {
        this.closeFloatingFilter();
      }
    }
  }

  getFilterOptions(field: string): string[] {
    if (this.filterColumnsData[field]) {
      const options = this.filterColumnsData[field] || [];
      const search = (this.filterSearch[field] || '').toLowerCase();
      const filtered = options.filter(opt => opt && String(opt).toLowerCase().includes(search));
      return filtered;
    }
    return [];
  }

  toggleMultiFilter(field: string, value: string, event: any): void {
    if (!this.multiFilters[field]) this.multiFilters[field] = [];
    const isChecked = event.target.checked;
    if (isChecked) {
      if (!this.multiFilters[field].includes(value))
        this.multiFilters[field].push(value);
    } else {
      this.multiFilters[field] = this.multiFilters[field].filter(v => v !== value);
    }
  }

  isChecked(field: string, value: string): boolean {
    return this.multiFilters[field]?.includes(value) || false;
  }

  getFilterType(field: string): 'multi-select' | 'date-range' | 'search' {
    const column = this.columns.find(col => col.field === field);
    return column?.filterType || 'multi-select';
  }

  async applyFilter(field: string): Promise<void> {
    const filterType = this.getFilterType(field);

    if (filterType === 'multi-select') {
      if (this.multiFilters[field]?.length === 1) {
        this.currentFilters[field] = this.multiFilters[field][0];
      } else if (this.multiFilters[field]?.length > 1) {
        this.currentFilters[field] = this.multiFilters[field].join(',');
      } else {
        this.currentFilters[field] = null;
      }
    }

    this.showFloatingFilter = false;
    this.activeFilter = '';
    this.loadFirstPage();
    await this.reloadFilterOptions();
  }

  applyDateRangeFilter(): void {
    this.showFloatingFilter = false;
    this.activeFilter = '';
    this.loadFirstPage();
  }

  clearDateRangeFilter(): void {
    this.dateRangeFilters.startDate = null;
    this.dateRangeFilters.endDate = null;
    this.showFloatingFilter = false;
    this.activeFilter = '';
    this.loadFirstPage();
  }

  applyCapacitySearch(): void {
    this.showFloatingFilter = false;
    this.activeFilter = '';
    this.loadFirstPage();
  }

  clearCapacitySearch(): void {
    this.capacitySearchFilter = null;
    this.showFloatingFilter = false;
    this.activeFilter = '';
    this.loadFirstPage();
  }

  async clearFilter(field: string): Promise<void> {
    const filterType = this.getFilterType(field);

    if (filterType === 'date-range') {
      this.clearDateRangeFilter();
      return;
    }

    if (filterType === 'search') {
      this.clearCapacitySearch();
      return;
    }

    this.multiFilters[field] = [];
    this.filterSearch[field] = '';
    this.currentFilters[field] = null;

    this.showFloatingFilter = false;
    this.activeFilter = '';
    this.loadFirstPage();
    await this.loadFilterOptionsInit();
  }

  hasAnyFilterApplied(): boolean {
    const hasCurrentFilter = Object.values(this.currentFilters).some(value => value !== null);
    const hasDateFilter = this.dateRangeFilters.startDate !== null || this.dateRangeFilters.endDate !== null;
    const hasCapacityFilter = this.capacitySearchFilter !== null && this.capacitySearchFilter !== '';
    return hasCurrentFilter || hasDateFilter || hasCapacityFilter;
  }

  async clearAllFilters(): Promise<void> {
    this.multiFilters = {};
    this.filterSearch = {};
    this.dateRangeFilters.startDate = null;
    this.dateRangeFilters.endDate = null;
    this.capacitySearchFilter = null;

    Object.keys(this.currentFilters).forEach(key => {
      this.currentFilters[key] = null;
    });

    this.activeFilter = '';
    this.showFloatingFilter = false;
    this.loadFirstPage();
    await this.loadFilterOptionsInit();
  }

  async exportToCSV(): Promise<void> {
    if (!this.data.length) {
      this.toastr.info('No data available to export.');
      return;
    }

    const worksheetData = this.data.map((row: AlternateWaterCapacityRow) => {
      const exportRow: Record<string, any> = {};

      this.columns.forEach((col) => {
        const value = row[col.field];
        exportRow[col.header] = Array.isArray(value) ? value.join(', ') : value ?? '';
      });

      return exportRow;
    });

    await exportRowsToExcel(
      worksheetData,
      this.columns.map((col) => col.header),
      'AlternateWaterCapacityData.xlsx',
      'AlternateWaterCapacity'
    );
  }

  openExportDialog(): void {
    this.dialog.open(this.exportOptionsDialog, {
      width: '380px',
      disableClose: true,
    });
  }

  async downloadAllData(): Promise<void> {
    try {
      const { blob, fileName } = await this.alternateWaterService.exportAllAlternateWaterCapacitiesData();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.click();
      URL.revokeObjectURL(url);
      this.toastr.success('Export downloaded successfully.');
    } catch (error: any) {
      this.toastr.error(error?.message || 'Failed to download export.');
    }
  }

  isFilterActive(field: string): boolean {
    const filterType = this.getFilterType(field);
    
    if (filterType === 'multi-select') {
      return (this.multiFilters[field] || []).length > 0;
    } else if (filterType === 'date-range') {
      return this.dateRangeFilters.startDate !== null || this.dateRangeFilters.endDate !== null;
    } else if (filterType === 'search') {
      return this.capacitySearchFilter !== null && this.capacitySearchFilter !== '';
    }
    
    return false;
  }

  generateRowId(): string { 
    return `row_${Date.now()}_${Math.random().toString(36).substr(2,9)}`; 
  }

  // Multi-select Dev Area methods
  getSelectedDevAreas(row: AlternateWaterCapacityRow): string[] {
    if (!row.devArea) return [];
    return row.devArea.split(',').filter(area => area.trim() !== '');
  }

  async addDevArea(row: AlternateWaterCapacityRow, event: Event): Promise<void> {
    if (!this.isIdentityFieldEditable(row)) return;

    const selected = (event.target as HTMLSelectElement).value;
    if (selected && !this.getSelectedDevAreas(row).includes(selected)) {
      const currentAreas = this.getSelectedDevAreas(row);
      currentAreas.push(selected);
      row.devArea = currentAreas.join(',');
      this.handleEdit(row);

      try {
        await this.refreshDropdownOptionsFromRowSelection(row);
      } catch (error: any) {
        this.toastr.error(error.message || 'Failed to refresh dropdown options', 'Error');
      }
    }
    (event.target as HTMLSelectElement).value = '';
  }

  async removeDevArea(row: AlternateWaterCapacityRow, areaToRemove: string): Promise<void> {
    if (!this.isIdentityFieldEditable(row)) return;

    const currentAreas = this.getSelectedDevAreas(row);
    const updatedAreas = currentAreas.filter(area => area !== areaToRemove);
    row.devArea = updatedAreas.join(',');
    this.handleEdit(row);

    try {
      await this.refreshDropdownOptionsFromRowSelection(row);
    } catch (error: any) {
      this.toastr.error(error.message || 'Failed to refresh dropdown options', 'Error');
    }
  }

  // Multi-select FE Asset Name methods
  getSelectedFeAssetNames(row: AlternateWaterCapacityRow): string[] {
    if (!row.feAssetName) return [];
    return row.feAssetName.split(',').filter(name => name.trim() !== '');
  }

  async addFeAssetName(row: AlternateWaterCapacityRow, event: Event): Promise<void> {
    if (!this.isIdentityFieldEditable(row)) return;

    const selected = (event.target as HTMLSelectElement).value;
    if (selected && !this.getSelectedFeAssetNames(row).includes(selected)) {
      const current = this.getSelectedFeAssetNames(row);
      current.push(selected);
      row.feAssetName = current.join(',');
      this.handleEdit(row);

      try {
        await this.refreshDropdownOptionsFromRowSelection(row);
      } catch (error: any) {
        this.toastr.error(error.message || 'Failed to refresh dropdown options', 'Error');
      }
    }
    (event.target as HTMLSelectElement).value = '';
  }

  async removeFeAssetName(row: AlternateWaterCapacityRow, nameToRemove: string): Promise<void> {
    if (!this.isIdentityFieldEditable(row)) return;

    const current = this.getSelectedFeAssetNames(row);
    const updated = current.filter(name => name !== nameToRemove);
    row.feAssetName = updated.join(',');
    this.handleEdit(row);

    try {
      await this.refreshDropdownOptionsFromRowSelection(row);
    } catch (error: any) {
      this.toastr.error(error.message || 'Failed to refresh dropdown options', 'Error');
    }
  }

  // Multi-select Disposal Outlet methods
  getSelectedDisposalOutlets(row: AlternateWaterCapacityRow): string[] {
    if (!row.disposalOutlet) return [];
    return row.disposalOutlet.split(',').filter(outlet => outlet.trim() !== '');
  }

  async addDisposalOutlet(row: AlternateWaterCapacityRow, event: Event): Promise<void> {
    if (!this.isIdentityFieldEditable(row)) return;

    const selected = (event.target as HTMLSelectElement).value;
    if (selected && !this.getSelectedDisposalOutlets(row).includes(selected)) {
      const current = this.getSelectedDisposalOutlets(row);
      current.push(selected);
      row.disposalOutlet = current.join(',');
      this.handleEdit(row);

      try {
        await this.refreshDropdownOptionsFromRowSelection(row);
      } catch (error: any) {
        this.toastr.error(error.message || 'Failed to refresh dropdown options', 'Error');
      }
    }
    (event.target as HTMLSelectElement).value = '';
  }

  async removeDisposalOutlet(row: AlternateWaterCapacityRow, outletToRemove: string): Promise<void> {
    if (!this.isIdentityFieldEditable(row)) return;

    const current = this.getSelectedDisposalOutlets(row);
    const updated = current.filter(outlet => outlet !== outletToRemove);
    row.disposalOutlet = updated.join(',');
    this.handleEdit(row);

    try {
      await this.refreshDropdownOptionsFromRowSelection(row);
    } catch (error: any) {
      this.toastr.error(error.message || 'Failed to refresh dropdown options', 'Error');
    }
  }

  private hasAllIdentityFieldsSelected(row: AlternateWaterCapacityRow): boolean {
    const hasDevArea = this.getSelectedDevAreas(row).length > 0;
    const hasFeAsset = this.getSelectedFeAssetNames(row).length > 0;
    const hasDisposalOutlet = this.getSelectedDisposalOutlets(row).length > 0;
    return hasDevArea && hasFeAsset && hasDisposalOutlet;
  }

  // Refresh dropdown options based on current row selection (cascading)
  private async refreshDropdownOptionsFromRowSelection(row: AlternateWaterCapacityRow): Promise<void> {
    if (this.hasAllIdentityFieldsSelected(row)) {
      return;
    }

    const dropdownData = await this.alternateWaterService.getAllDropdownOptions({
      developmentArea: row.devArea || null,
      feAssetName: row.feAssetName || null,
      disposalOutletName: row.disposalOutlet || null,
    });

    const selectedDevAreas = this.getSelectedDevAreas(row);
    const apiDevAreas = dropdownData.devAreas || [];
    this.devAreaOptions = Array.from(new Set([
      ...this.devAreaOptions,
      ...apiDevAreas,
      ...selectedDevAreas,
    ])).filter(area => !!area && area.trim() !== '');

    const selectedFeAssets = this.getSelectedFeAssetNames(row);
    const apiFeAssets = dropdownData.feAssetNames || [];
    this.feAssetNameOptions = Array.from(new Set([
      ...this.feAssetNameOptions,
      ...apiFeAssets,
      ...selectedFeAssets,
    ])).filter(name => !!name && name.trim() !== '');

    const selectedOutlets = this.getSelectedDisposalOutlets(row);
    const apiOutlets = dropdownData.disposalOutlets || [];
    this.disposalOutletOptions = Array.from(new Set([
      ...this.disposalOutletOptions,
      ...apiOutlets,
      ...selectedOutlets,
    ])).filter(outlet => !!outlet && outlet.trim() !== '');

    this.alternativeCapacityTypeOptions = dropdownData.alternativeCapacityTypes || [];
  }

  // Refresh dropdown options based on current filter selection
  private async refreshDropdownOptionsFromCurrentSelection(): Promise<void> {
    const dropdownData = await this.alternateWaterService.getAllDropdownOptions({
      developmentArea: this.currentFilters['devArea'] || null,
      feAssetName: this.currentFilters['feAssetName'] || null,
      disposalOutletName: this.currentFilters['disposalOutlet'] || null,
    });

    this.devAreaOptions = dropdownData.devAreas || [];
    this.feAssetNameOptions = dropdownData.feAssetNames || [];
    this.disposalOutletOptions = dropdownData.disposalOutlets || [];
    this.alternativeCapacityTypeOptions = dropdownData.alternativeCapacityTypes || [];
  }

  // Helper: ensure current row value is in the options list
  private withCurrentValue(options: string[], value: string): string[] {
    if (!value) return options;
    return options.includes(value) ? options : [value, ...options];
  }

  getFeAssetNameOptionsForRow(row: AlternateWaterCapacityRow): string[] {
    return this.withCurrentValue(this.feAssetNameOptions, row.feAssetName);
  }

  getDisposalOutletOptionsForRow(row: AlternateWaterCapacityRow): string[] {
    return this.withCurrentValue(this.disposalOutletOptions, row.disposalOutlet);
  }

  // Load initial dropdowns for new row (reset to unfiltered)
  private async loadInitialDropdownsForNewRow(): Promise<void> {
    try {
      this.spinnerBoolean = true;
      const dropdownData = await this.alternateWaterService.getAllDropdownOptions({
        developmentArea: null,
        feAssetName: null,
        disposalOutletName: null,
      });

      this.devAreaOptions = dropdownData.devAreas || [];
      this.feAssetNameOptions = dropdownData.feAssetNames || [];
      this.disposalOutletOptions = dropdownData.disposalOutlets || [];
      this.alternativeCapacityTypeOptions = dropdownData.alternativeCapacityTypes || [];
    } catch (error: any) {
      this.toastr.error(error.message || 'Failed to load dropdown options', 'Error');
    } finally {
      this.spinnerBoolean = false;
    }
  }
}